import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance2";
import { API_BASE_URL } from "../config/apiConfig";

import { Alert, Button, Col, Form, Input, Row, Skeleton, Switch, Typography, Upload } from "antd";
import { FiPaperclip, FiUploadCloud } from "react-icons/fi";
import { RowActions, SectionCard, StatusTag, TabBar, useFlatPage } from "../components/ui";
import notify from "../utils/notify";

const { Text } = Typography;
const { TextArea } = Input;

const KNOWN = [
  { name: "BookingConfirmation", label: "Booking Confirmation" },
  { name: "CheckIn", label: "Check-In" },
  { name: "CheckOut", label: "Check-Out" },
];

// tolerant getters (Pascal/camel)
const val = (o, pascal, camel, fallback = undefined) => o?.[pascal] ?? o?.[camel] ?? fallback;
const getName = (t) => val(t, "TemplateName", "templateName", "");
const getId = (t) => val(t, "EmailTemplateId", "emailTemplateId", null);
const getSubject = (t) => val(t, "Subject", "subject", "");
const getHtml = (t) => val(t, "HtmlContent", "htmlContent", "");
const getDesc = (t) => val(t, "Description", "description", "");
const getHasAtt = (t) => val(t, "HasAttachment", "hasAttachment", false);
const getPattern = (t) => val(t, "AttachmentNamePattern", "attachmentNamePattern", "");
const getAttachments = (t) => val(t, "Attachments", "attachments", []) || [];

const getAttId = (a) => val(a, "EmailTemplateAttachmentId", "emailTemplateAttachmentId", null);
const getAttName = (a) => val(a, "FileName", "fileName", "");
const getAttPath = (a) => val(a, "FilePath", "filePath", "");
const getAttType = (a) => val(a, "ContentType", "contentType", "file");

// normalize relative -> absolute
const toAbs = (u) => {
  if (!u) return null;
  if (/^https?:\/\//i.test(u)) return u;
  const base = (API_BASE_URL || "").replace(/\/+$/, "");
  return u.startsWith("/") ? `${base}${u}` : `${base}/${u}`;
};

const HotelSettings = () => {
  useFlatPage();
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [logoUrl, setLogoUrl] = useState(null);
  const [fileUploading, setFileUploading] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [tab, setTab] = useState("branding");
  const [form] = Form.useForm();

  const seedFormFrom = (items) => {
    const init = {};
    (items || []).forEach((t) => {
      const name = getName(t);
      if (!name) return;
      init[`${name}_Subject`] = getSubject(t);
      init[`${name}_HtmlContent`] = getHtml(t);
      init[`${name}_Description`] = getDesc(t);
      init[`${name}_HasAttachment`] = !!getHasAtt(t);
      init[`${name}_AttachmentNamePattern`] = getPattern(t) ?? "";
    });
    KNOWN.forEach(({ name }) => {
      init[`${name}_Subject`] ??= "";
      init[`${name}_HtmlContent`] ??= "";
      init[`${name}_Description`] ??= "";
      init[`${name}_HasAttachment`] ??= false;
      init[`${name}_AttachmentNamePattern`] ??= "";
    });
    form.setFieldsValue(init);
  };

  useEffect(() => {
    if (!hotelId) return;

    const load = async () => {
      try {
        setLoading(true);

        // fetch persisted logo (see backend GET /Hotel/{id}/logo below)
        try {
          const r = await axiosInstance.get(`/Hotel/${hotelId}/logo`);
          const raw = r?.data?.logoUrl ?? r?.data?.LogoUrl;
          if (raw) setLogoUrl(toAbs(raw));
        } catch {
          // optional fallback to GET /Hotel/{id} if you store LogoUrl on the hotel row
          try {
            const r2 = await axiosInstance.get(`/Hotel/${hotelId}`);
            const raw2 = r2?.data?.logoUrl ?? r2?.data?.LogoUrl ?? r2?.data?.logoURL;
            if (raw2) setLogoUrl(toAbs(raw2));
          } catch {}
        }

        const { data } = await axiosInstance.get(`/EmailTemplates/${hotelId}`);
        const list = Array.isArray(data) ? data : [];
        setTemplates(list);
        seedFormFrom(list);
      } catch (err) {
        notify.error(err?.response?.data?.message || "Failed to load settings");
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  const byName = useMemo(() => {
    const m = new Map();
    for (const t of templates) m.set(getName(t), t);
    return m;
  }, [templates]);

  // POST /Hotel/{id}/hotellogo
  const handleLogoUpload = async ({ file }) => {
    if (!hotelId) return;
    setFileUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // let Axios set the multipart boundary — no manual Content-Type
      const { data } = await axiosInstance.post(`/Hotel/${hotelId}/hotellogo`, fd);
      const raw = data?.logoUrl ?? data?.LogoUrl;
      setLogoUrl(raw ? `${toAbs(raw)}?v=${Date.now()}` : null); // cache-bust
      notify.success("Logo updated");
    } catch (err) {
      notify.error(err?.response?.data?.message || "Upload failed");
    } finally {
      setFileUploading(false);
    }
  };

  const onSaveTemplates = async () => {
    try {
      const values = await form.validateFields();
      const payload = KNOWN.map((k) => ({
        HotelId: hotelId,
        TemplateName: k.name,
        Subject: values[`${k.name}_Subject`],
        HtmlContent: values[`${k.name}_HtmlContent`],
        Description: values[`${k.name}_Description`] || "",
        HasAttachment: !!values[`${k.name}_HasAttachment`],
        AttachmentNamePattern: values[`${k.name}_AttachmentNamePattern`] || "",
      }));

      setSaving(true);
      await axiosInstance.put(`/EmailTemplates/${hotelId}`, payload);
      notify.success("Templates saved");

      const { data: fresh } = await axiosInstance.get(`/EmailTemplates/${hotelId}`);
      const list = Array.isArray(fresh) ? fresh : [];
      setTemplates(list);
      seedFormFrom(list);
    } catch (err) {
      if (!err?.errorFields) {
        notify.error(err?.response?.data?.message || "Failed to save templates");
      }
    } finally {
      setSaving(false);
    }
  };

  const uploadAttachments = async (templateName, files) => {
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const { data } = await axiosInstance.post(
        `/EmailTemplates/${hotelId}/${templateName}/attachments`,
        fd
      );
      const newAtts = Array.isArray(data) ? data : [data];
      setTemplates((prev) =>
        prev.map((t) =>
          getName(t) !== templateName
            ? t
            : { ...t, Attachments: [...getAttachments(t), ...newAtts] }
        )
      );
      notify.success("Attachment(s) uploaded");
    } catch (err) {
      notify.error(err?.response?.data?.message || "Attachment upload failed");
    }
  };

  const deleteAttachment = async (t, att) => {
    try {
      const templateId = getId(t);
      const attId = getAttId(att);
      if (templateId == null || attId == null) {
        notify.error("Missing attachment identifiers.");
        return;
      }
      await axiosInstance.delete(`/EmailTemplates/${hotelId}/${templateId}/attachments/${attId}`);
      setTemplates((prev) =>
        prev.map((x) =>
          getId(x) !== templateId
            ? x
            : { ...x, Attachments: getAttachments(x).filter((a) => getAttId(a) !== attId) }
        )
      );
      notify.success("Attachment deleted");
    } catch (err) {
      notify.error(err?.response?.data?.message || "Delete failed");
    }
  };

  const emailHelp = (
    <Alert
      type="info"
      showIcon
      title="Placeholders"
      description={
        <div>
          Use <Text code>{"{{TokenName}}"}</Text> in the subject or body. Common tokens:
          <div style={{ marginTop: 8 }}>
            <Text code>HotelName</Text>, <Text code>BookingNo</Text>, <Text code>GuestFirstName</Text>,{" "}
            <Text code>GuestLastName</Text>, <Text code>CheckInDate</Text>, <Text code>CheckOutDate</Text>,{" "}
            <Text code>RoomNumber</Text>, <Text code>Currency</Text>, <Text code>TotalAmount</Text>,{" "}
            <Text code>PaidAmount</Text>, <Text code>Balance</Text>
          </div>
        </div>
      }
      style={{ marginBottom: 16 }}
    />
  );

  if (!hotelId) {
    return <Alert type="warning" showIcon title="Please log in" description="Hotel ID not found." />;
  }

  return (
    <div className="hms-page hs-page">
      <SectionCard flush>
        <TabBar
          tabs={[
            { key: "branding", label: "Branding" },
            { key: "emails", label: "Email templates" },
          ]}
          value={tab}
          onChange={setTab}
        />
      </SectionCard>

      {loading ? (
        <div style={{ marginTop: 16 }}>
          <SectionCard>
            <Skeleton active />
          </SectionCard>
        </div>
      ) : (
        <div className="hms-stack" style={{ marginTop: 16 }}>
          {tab === "branding" && (
            <SectionCard title="Hotel logo">
              <p className="hms-modal-note">Shown on invoices, emails and the guest pages.</p>
              <div className="hms-grid-2" style={{ gridTemplateColumns: "220px 1fr", alignItems: "start" }}>
                <div>
                  <div className="hms-detail-label hms-cell-sub" style={{ marginBottom: 8 }}>
                    Current logo
                  </div>
                  <div
                    style={{
                      width: 180,
                      height: 180,
                      borderRadius: 14,
                      border: "1px solid var(--hms-border)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      background: "#fafafb",
                    }}
                  >
                    {logoUrl ? (
                      <img src={logoUrl} alt="Hotel logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <span className="hms-muted">No logo yet</span>
                    )}
                  </div>
                </div>

                <div>
                  <div className="hms-cell-sub" style={{ marginBottom: 8 }}>
                    Upload a new logo
                  </div>
                  <Upload.Dragger
                    multiple={false}
                    accept=".png,.jpg,.jpeg,.gif,.webp"
                    customRequest={({ file, onSuccess, onError }) => {
                      handleLogoUpload({ file }).then(() => onSuccess?.("ok")).catch(onError);
                    }}
                    showUploadList={false}
                    disabled={fileUploading}
                  >
                    <p className="ant-upload-drag-icon">
                      <FiUploadCloud size={28} />
                    </p>
                    <p className="ant-upload-text">{fileUploading ? "Uploading…" : "Click or drag a file here"}</p>
                    <p className="ant-upload-hint">PNG, JPG, GIF or WebP, up to 10 MB</p>
                  </Upload.Dragger>
                </div>
              </div>
            </SectionCard>
          )}

          {/* The form stays mounted so the values loaded above are never lost when switching tabs */}
          <div style={{ display: tab === "emails" ? "block" : "none" }}>
            {emailHelp}
            <Form layout="vertical" form={form}>
              <div className="hms-stack">
                {KNOWN.map(({ name, label }) => {
                  const t = byName.get(name);
                  const atts = getAttachments(t);
                  return (
                    <SectionCard key={name} title={label}>
                      <Row gutter={24}>
                        <Col xs={24} md={10}>
                          <Form.Item
                            label="Subject"
                            name={`${name}_Subject`}
                            rules={[{ required: true, message: "Subject is required" }, { max: 200, message: "Max 200 characters" }]}
                          >
                            <Input placeholder="Subject" />
                          </Form.Item>

                          <Form.Item label="Description" name={`${name}_Description`}>
                            <Input placeholder="Optional short description" />
                          </Form.Item>

                          <Form.Item label="Include an attachment" name={`${name}_HasAttachment`} valuePropName="checked">
                            <Switch />
                          </Form.Item>

                          <Form.Item label="Attachment file name" name={`${name}_AttachmentNamePattern`}>
                            <Input placeholder="e.g. Invoice_{{BookingNo}}.pdf" />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={14}>
                          <Form.Item
                            label="Body (HTML supported)"
                            name={`${name}_HtmlContent`}
                            rules={[{ required: true, message: "Body is required" }]}
                          >
                            <TextArea rows={8} placeholder="<p>Hello {{GuestFirstName}}...</p>" />
                          </Form.Item>

                          <div className="hms-dsection-head" style={{ marginTop: 4 }}>
                            <h4>Attachments</h4>
                            <Upload
                              multiple
                              beforeUpload={() => false}
                              showUploadList={false}
                              accept=".pdf,.png,.jpg,.jpeg,.webp"
                              onChange={(info) => {
                                const files = info.fileList.map((f) => f.originFileObj).filter(Boolean);
                                if (files.length) {
                                  uploadAttachments(name, files);
                                  info.fileList.length = 0;
                                }
                              }}
                            >
                              <Button>Upload attachments</Button>
                            </Upload>
                          </div>

                          {atts.length === 0 ? (
                            <div className="hms-empty">No attachments</div>
                          ) : (
                            <ul className="hms-checklist">
                              {atts.map((att) => {
                                const href = toAbs(getAttPath(att));
                                return (
                                  <li key={getAttId(att) ?? getAttName(att)}>
                                    <span className="hms-check">
                                      <FiPaperclip />
                                      <span>
                                        {href ? (
                                          <a href={href} target="_blank" rel="noreferrer">
                                            {getAttName(att)}
                                          </a>
                                        ) : (
                                          getAttName(att)
                                        )}
                                      </span>
                                      <StatusTag status="">{getAttType(att) || "file"}</StatusTag>
                                    </span>
                                    <RowActions
                                      actions={[
                                        {
                                          key: "delete",
                                          label: "Delete attachment",
                                          icon: "delete",
                                          danger: true,
                                          confirm: "Delete this attachment?",
                                          okText: "Delete",
                                          onClick: () => deleteAttachment(t, att),
                                        },
                                      ]}
                                    />
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </Col>
                      </Row>
                    </SectionCard>
                  );
                })}

                <div className="hms-form-actions">
                  <Button
                    onClick={() => {
                      seedFormFrom(templates);
                      notify.info("Reverted unsaved changes.");
                    }}
                  >
                    Revert
                  </Button>
                  <Button type="primary" loading={saving} onClick={onSaveTemplates}>
                    Save templates
                  </Button>
                </div>
              </div>
            </Form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelSettings;
